import { useEffect, useState } from "react";
import type { Chair, DayRoster, StaffMember } from "@dentacore/shared-types";

import { assignChair, createChair, unassignChair, updateChair } from "../api/appointments";
import { useCan } from "../auth/use-can";
import { Modal } from "../ui/modal";
import { Select } from "../ui/select";
import { RowMenu } from "../ui/popovers";
import styles from "./edit-chairs.module.css";

/**
 * Add a chair, retire one, edit who is on it, edit what each one is called and
 * — one dialog behind one button (client
 * request, 2026-08-24, the first four grouped under a single "Edit chairs"
 * menu in the same spec paragraph).
 *
 * **Turnover is no longer here** (2026-10-11, decision NT1). It was a per-chair
 * number (TC1, 2026-09-23); Anthony asked for it to be removed altogether, so
 * a chair has a name and an order and nothing else to configure.
 *
 * **Every write here already existed server-side.** `POST/PATCH /chairs` and
 * `PATCH /branches/:id` were built along with the rest of the admin surface
 * and had no screen calling them; `assignChair`/`unassignChair` are the same
 * two calls `chair-roster.tsx` used before STORY-031 removed its always-on
 * strip for exact mockup fidelity. This is new UI over old endpoints, not a
 * new capability — the roster-editing half is close kin to that deleted
 * component, folded into a menu instead of a permanent panel.
 *
 * **Retiring, not deleting.** `PATCH /chairs/:id { isActive: false }` is the
 * only removal path there is — chairs have no delete endpoint, so a chair
 * that already carries appointments keeps them, and a retired chair simply
 * stops appearing as a column.
 *
 * **Two guards, not one, since decision S1.** The warning the spec asked for
 * ("are you sure") shows for a chair with a *history* — `appointmentCount > 0`
 * and nothing ahead of it. A chair with bookings **still to come** is refused
 * by the server with the count in the message, so this dialog does not ask
 * about it: it sends the write and shows what comes back. The same pair of
 * rules is on the Settings screen, and both read them from the same two counts
 * on `ChairDto`.
 */
export function EditChairs({
  branchId,
  chairs,
  day,
  roster,
  staff,
  onChanged,
  onClose,
}: {
  branchId: string;
  chairs: readonly Chair[];
  day: string;
  roster: DayRoster | null;
  staff: readonly StaffMember[];
  onChanged: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const canRoster = useCan()("calendar.roster");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newChairName, setNewChairName] = useState("");
  /** Optional: who is on the new chair for the day shown, set in the same step. */
  const [newChairUserId, setNewChairUserId] = useState("");
  const [confirmingRetireId, setConfirmingRetireId] = useState<string | null>(null);
  const [editingRosterFor, setEditingRosterFor] = useState<string | null>(null);
  const [rosterUserId, setRosterUserId] = useState("");
  /** Which chair's row is open for editing, and the two fields it holds. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState("");

  /**
   * The chair list as it is being reordered by dragging (client request,
   * 2026-08-28: "I should be able to edit chair order … by dragging them").
   *
   * Held locally so a drag reorders the list on screen at once; the server's
   * order is adopted back whenever the prop changes and no drag is in progress,
   * so a reload or a change from elsewhere is not fought.
   */
  const [ordered, setOrdered] = useState<readonly Chair[]>(chairs);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  useEffect(() => {
    if (dragId === null) setOrdered(chairs);
  }, [chairs, dragId]);

  /**
   * Write a whole new left-to-right order, renumbering from zero — the same
   * shape `branch-page.tsx` uses, and for the same reason: chairs created
   * before ordering existed can share a `sortOrder`, so swapping two values
   * moves nothing. Only the rows that actually changed are sent.
   */
  const persistOrder = (next: readonly Chair[]): void => {
    setOrdered(next);
    setBusy(true);
    setError(null);

    void (async () => {
      for (const [index, chair] of next.entries()) {
        if (chair.sortOrder !== index) await updateChair(chair.id, { sortOrder: index });
      }
    })()
      .then(() => onChanged())
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That change did not save.");
        setOrdered(chairs);
      })
      .finally(() => setBusy(false));
  };

  const dropOnto = (targetId: string): void => {
    const from = ordered.findIndex((row) => row.id === dragId);
    const to = ordered.findIndex((row) => row.id === targetId);
    setDragId(null);
    setDragOverId(null);
    if (from === -1 || to === -1 || from === to) return;

    const next = [...ordered];
    const [moved] = next.splice(from, 1);
    if (moved !== undefined) next.splice(to, 0, moved);
    persistOrder(next);
  };

  /**
   * Resolves `true` on success, `false` on a handled failure — never rejects,
   * so a caller can chain success-only cleanup (closing an inline editor, say)
   * with `.then((ok) => ok && ...)` instead of every call site needing its own
   * `.catch()` to avoid an unhandled rejection.
   */
  const apply = (work: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);

    return work()
      .then(() => {
        onChanged();
        return true;
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That change did not save.");
        return false;
      })
      .finally(() => setBusy(false));
  };

  // Only this branch's members can be rostered — matches what the server
  // enforces, which refuses somebody who is not a member because rostering
  // them would produce a header naming a person the calendar never shows
  // anything for.
  const rosterable = staff.filter(
    (person) =>
      person.isActive && person.memberships.some((membership) => membership.branchId === branchId),
  );

  /**
   * Whether the open edit row may be saved: it has a name. (It also used to
   * guard the turnover field, QA20-13, which is gone.)
   */
  const editableRowIsValid = nameInput.trim() !== "";

  /** Open a chair's row with its current name in the field. */
  const openEditor = (chair: Chair): void => {
    setNameInput(chair.name);
    setEditingId(chair.id);
  };

  return (
    <Modal label="Edit chairs" testId="edit-chairs" onClose={onClose}>
      <h2 className={styles.title}>Edit chairs</h2>

      {error !== null && (
        <p className={styles.error} data-testid="edit-chairs-error">
          {error}
        </p>
      )}

      <section className={styles.section}>
        <span className={styles.sectionLabel}>Chairs</span>
        <p className={styles.hint}>
          Drag a chair by its handle to change the order its column draws in. Edit one to rename it.
        </p>
        <div className={styles.chairList}>
          {ordered.map((chair) => {
            const rosterChair = roster?.chairs.find((row) => row.chairId === chair.id) ?? null;
            const rowBusy =
              editingId === chair.id ||
              confirmingRetireId === chair.id ||
              editingRosterFor === chair.id;

            return (
              <div
                key={chair.id}
                className={styles.chairRow}
                data-testid="edit-chairs-row"
                data-chair-id={chair.id}
                data-dragging={dragId === chair.id ? "true" : undefined}
                data-drag-over={dragOverId === chair.id && dragId !== chair.id ? "true" : undefined}
                onDragOver={(event) => {
                  if (dragId === null || dragId === chair.id) return;
                  event.preventDefault();
                  setDragOverId(chair.id);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  dropOnto(chair.id);
                }}
              >
                <span
                  className={styles.grip}
                  aria-hidden="true"
                  data-testid="chair-grip"
                  draggable={!busy && !rowBusy}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = "move";
                    setDragId(chair.id);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setDragOverId(null);
                  }}
                >
                  ⠿
                </span>
                {editingId === chair.id ? (
                  <div className={styles.renameRow}>
                    <input
                      className={styles.nameInput}
                      value={nameInput}
                      onChange={(event) => setNameInput(event.target.value)}
                      aria-label="Chair name"
                      data-testid="chair-name-input"
                    />
                    <button
                      type="button"
                      className={styles.removeChip}
                      disabled={busy || !editableRowIsValid}
                      onClick={() =>
                        apply(() => updateChair(chair.id, { name: nameInput.trim() })).then(
                          (ok) => ok && setEditingId(null),
                        )
                      }
                      data-testid="chair-rename-save"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className={styles.removeChip}
                      disabled={busy}
                      onClick={() => setEditingId(null)}
                      data-testid="chair-rename-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className={styles.chairNameRow}>
                    <span className={styles.chairNameGroup}>
                      <span
                        className={chair.isActive ? styles.chairName : styles.chairNameRetired}
                        data-testid="chair-name"
                      >
                        {chair.name}
                        {!chair.isActive && " — retired"}
                      </span>
                    </span>
                    {/*
                      **Edit and Remove behind ⋯** (decision CS10). **Edit, not
                      Rename** (Anthony QA #12) — the control opens the row for
                      editing. Remove keeps its
                      confirmation for a chair with history.
                    */}
                    <RowMenu
                      label={`More for ${chair.name}`}
                      testId="chair-menu"
                      items={[
                        {
                          label: "Edit",
                          testId: "chair-rename",
                          disabled: busy,
                          onSelect: () => openEditor(chair),
                        },
                        ...(chair.isActive
                          ? [
                              {
                                label: "Remove",
                                testId: "chair-remove",
                                danger: true,
                                disabled: busy,
                                onSelect: () => {
                                  // The confirm is for a long history. Bookings
                                  // still to come are refused by the server
                                  // outright (decision S1), so this sends the
                                  // write and lets the 409 arrive as a sentence.
                                  if (
                                    chair.appointmentCount > 0 &&
                                    chair.futureAppointmentCount === 0
                                  ) {
                                    setConfirmingRetireId(chair.id);
                                    return;
                                  }
                                  void apply(() => updateChair(chair.id, { isActive: false }));
                                },
                              },
                            ]
                          : []),
                      ]}
                    />
                  </div>
                )}

                {/* Who sits in a chair is `calendar.roster`'s (QA third run). */}
                {canRoster && chair.isActive && rosterChair !== null && (
                  <div className={styles.rosterActions}>
                    {rosterChair.assignments.map((assignment) => (
                      <button
                        key={assignment.id}
                        type="button"
                        className={styles.removeChip}
                        disabled={busy}
                        title={`Take ${assignment.providerName} off this chair`}
                        onClick={() => apply(() => unassignChair(assignment.id))}
                        data-testid="unassign"
                      >
                        {assignment.providerName} ×
                      </button>
                    ))}

                    {editingRosterFor === chair.id ? (
                      <>
                        {/*
                          Searchable, for the same reason the booking form's
                          provider picker is: this is the clinic's whole
                          clinical staff, and a practice with twenty dentists
                          cannot scroll to one by eye.
                        */}
                        <div className={styles.rosterPicker}>
                          <Select
                            id="roster-who"
                            label="Who is on this chair"
                            value={rosterUserId}
                            options={rosterable.map((person) => ({
                              value: person.id,
                              label: `${person.firstName} ${person.lastName}`,
                              keywords: person.role,
                            }))}
                            onChange={setRosterUserId}
                            testId="roster-who"
                            placeholder="Choose…"
                          />
                        </div>
                        <button
                          type="button"
                          className={styles.removeChip}
                          disabled={busy || rosterUserId === ""}
                          onClick={() =>
                            apply(() =>
                              assignChair({ chairId: chair.id, userId: rosterUserId, date: day }),
                            ).then((ok) => {
                              if (!ok) return;
                              setEditingRosterFor(null);
                              setRosterUserId("");
                            })
                          }
                          data-testid="roster-save"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className={styles.removeChip}
                          disabled={busy}
                          onClick={() => setEditingRosterFor(null)}
                          data-testid="roster-cancel"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className={styles.removeChip}
                        disabled={busy}
                        onClick={() => {
                          setRosterUserId("");
                          setEditingRosterFor(chair.id);
                        }}
                        data-testid="roster-add"
                      >
                        + Who is on this chair
                      </button>
                    )}
                  </div>
                )}

                {confirmingRetireId === chair.id ? (
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmText}>
                      Remove {chair.name}? It has {chair.appointmentCount} appointment
                      {chair.appointmentCount === 1 ? "" : "s"} on it.
                    </span>
                    <button
                      type="button"
                      className={styles.warm}
                      disabled={busy}
                      onClick={() =>
                        apply(() => updateChair(chair.id, { isActive: false })).then(
                          (ok) => ok && setConfirmingRetireId(null),
                        )
                      }
                      data-testid="chair-retire-confirm"
                    >
                      Yes, remove it
                    </button>
                    <button
                      type="button"
                      className={styles.removeChip}
                      disabled={busy}
                      onClick={() => setConfirmingRetireId(null)}
                      data-testid="chair-retire-cancel"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  // A retired chair keeps *Reactivate* on the row: it is the one
                  // thing anybody opens a retired chair to do.
                  !chair.isActive && (
                    <div className={styles.chairActions}>
                      <button
                        type="button"
                        className={styles.removeChip}
                        disabled={busy}
                        onClick={() => apply(() => updateChair(chair.id, { isActive: true }))}
                        data-testid="chair-reactivate"
                      >
                        Reactivate
                      </button>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className={styles.section}>
        <span className={styles.sectionLabel}>Add a chair</span>
        <div className={styles.addRow}>
          <input
            className={styles.nameInput}
            value={newChairName}
            onChange={(event) => setNewChairName(event.target.value)}
            placeholder="Whatever the clinic calls this room"
            aria-label="New chair name"
            data-testid="new-chair-name"
          />
          {canRoster && (
            <div className={styles.rosterPicker}>
              <Select
                id="new-chair-who"
                label="Who is on this chair"
                value={newChairUserId}
                options={rosterable.map((person) => ({
                  value: person.id,
                  label: `${person.firstName} ${person.lastName}`,
                  keywords: person.role,
                }))}
                onChange={setNewChairUserId}
                testId="new-chair-who"
                placeholder="Who is on it…"
              />
            </div>
          )}
          <button
            type="button"
            className={styles.primary}
            disabled={busy || newChairName.trim() === ""}
            onClick={() =>
              // The chair, then — if someone was chosen — them on it for the day
              // shown, so there is no second trip to "+ Who is on this chair".
              apply(async () => {
                const created = await createChair({ name: newChairName.trim() });
                // Cleared now, so a refused assignment below cannot leave the
                // name in the box for a second click to create a duplicate.
                setNewChairName("");
                if (newChairUserId !== "") {
                  await assignChair({ chairId: created.id, userId: newChairUserId, date: day });
                }
              }).then((ok) => {
                if (!ok) return;
                setNewChairName("");
                setNewChairUserId("");
              })
            }
            data-testid="add-chair"
          >
            Add chair
          </button>
        </div>
      </section>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.secondary}
          onClick={onClose}
          data-testid="edit-chairs-close"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
